/**
 * Type-Safe SessionStorage Utility
 *
 * Provides type-safe access to sessionStorage with:
 * - Automatic JSON serialization/deserialization
 * - Error handling with graceful fallbacks
 * - Optional TTL (time-to-live) support
 * - Integration with existing sessionStorage cleanup
 *
 * @module sessionStore
 */

import { STORAGE_KEYS } from '../constants';
import { safeSetItem, safeRemoveItem } from './sessionStorageCleanup';
import { handleSessionStorageError } from '@ui/utils/errorHandling';

// =============================================================================
// Types
// =============================================================================

/**
 * Correlation ID with optional expiration
 */
export interface CorrelationData {
    value: string;
    expires?: number;
}

// =============================================================================
// Private Helpers
// =============================================================================

/**
 * Safely parse JSON with error handling
 */
function safeParse<T>(raw: string | null, conversationId: string): T | null {
    if (!raw) return null;

    try {
        return JSON.parse(raw) as T;
    } catch (error) {
        handleSessionStorageError(error, 'parse', conversationId);
        return null;
    }
}

/**
 * Check if data with TTL has expired
 */
function isExpired(expires: number | undefined): boolean {
    if (!expires) return false;
    return Date.now() > expires;
}

// =============================================================================
// SessionStore API
// =============================================================================

/**
 * Type-safe sessionStorage accessor for Chat feature
 */
export const sessionStore = {
    // =========================================================================
    // Correlation ID (Active Streams)
    // =========================================================================

    /**
     * Store correlation ID for active stream
     * @param conversationId - Conversation ID
     * @param correlationId - Correlation ID for SSE stream
     * @param ttl - Optional time-to-live in milliseconds (default: no expiration)
     */
    setCorrelation(conversationId: string, correlationId: string, ttl?: number): boolean {
        const data: CorrelationData = {
            value: correlationId,
            expires: ttl ? Date.now() + ttl : undefined,
        };

        return safeSetItem(STORAGE_KEYS.CORRELATION(conversationId), JSON.stringify(data));
    },

    /**
     * Retrieve correlation ID from storage
     * Returns null if not found or expired
     */
    getCorrelation(conversationId: string): string | null {
        const raw = sessionStorage.getItem(STORAGE_KEYS.CORRELATION(conversationId));
        const data = safeParse<CorrelationData>(raw, conversationId);

        if (!data) return null;

        // Check expiration if TTL was set
        if (isExpired(data.expires)) {
            // Clean up expired data
            this.removeCorrelation(conversationId);
            return null;
        }

        return data.value;
    },

    /**
     * Remove correlation ID from storage
     */
    removeCorrelation(conversationId: string): boolean {
        return safeRemoveItem(STORAGE_KEYS.CORRELATION(conversationId));
    },

    // =========================================================================
    // Batch Operations
    // =========================================================================

    /**
     * Remove all session data for a conversation
     * Useful for cleanup after conversation ends or on error
     */
    clearConversation(conversationId: string): void {
        this.removeCorrelation(conversationId);
    },

    /**
     * Check if a conversation has any active session data
     */
    hasActiveData(conversationId: string): boolean {
        return this.getCorrelation(conversationId) !== null;
    },

    // =========================================================================
    // Migration Helpers (Backward Compatibility)
    // =========================================================================

    /**
     * Migrate old correlation format (plain string) to new format (with expiration)
     * @internal - For migration only
     */
    migrateCorrelation(conversationId: string): void {
        const raw = sessionStorage.getItem(STORAGE_KEYS.CORRELATION(conversationId));
        if (!raw) return;

        try {
            // Try parsing as new format first
            const parsed = JSON.parse(raw) as CorrelationData;
            if (typeof parsed === 'object' && 'value' in parsed) {
                // Already in new format
                return;
            }
        } catch {
            // If JSON parse fails, it's a plain string (old format)
            // Convert to new format
            this.setCorrelation(conversationId, raw);
        }
    },
} as const;

// =============================================================================
// Exports
// =============================================================================

export default sessionStore;
